import { apiClient } from '@/lib/api-client';

/**
 * Admin Navigation API. Reads the full editing tree (incl. hidden items) and
 * mutates menus/items. Reorder sends the full flattened list of
 * {id, parentId, sortOrder} so a drag is one atomic call.
 */

export interface NavItem {
  id: string;
  label: string;
  href: string | null;
  pageId: string | null;
  openInNewTab: boolean;
  isVisible: boolean;
  sortOrder: number;
  children: NavItem[];
}

export interface NavTree {
  location: string;
  menuId: string | null;
  title: string | null;
  items: NavItem[];
}

const BASE = '/admin/website/navigation';

export const getNavTree = async (location: string): Promise<NavTree> =>
  (await apiClient.get<NavTree>(`${BASE}/${location}`)).data;

export const createMenu = async (location: string, title: string): Promise<{ id: string }> =>
  (await apiClient.post<{ id: string }>(`${BASE}/menus`, { location, title })).data;

export interface CreateItemInput {
  menuId: string;
  parentId?: string | null;
  label: string;
  href?: string | null;
  pageId?: string | null;
  openInNewTab?: boolean;
  sortOrder?: number;
  isVisible?: boolean;
}

export const createItem = async (input: CreateItemInput): Promise<{ id: string }> =>
  (await apiClient.post<{ id: string }>(`${BASE}/items`, input)).data;

export interface UpdateItemInput {
  label?: string;
  href?: string | null;
  pageId?: string | null;
  openInNewTab?: boolean;
  isVisible?: boolean;
  parentId?: string | null;
  sortOrder?: number;
}

export const updateItem = async (id: string, input: UpdateItemInput): Promise<void> => {
  await apiClient.patch(`${BASE}/items/${id}`, input);
};

export const deleteItem = async (id: string): Promise<void> => {
  await apiClient.delete(`${BASE}/items/${id}`);
};

export const reorderItems = async (
  menuId: string,
  items: { id: string; parentId: string | null; sortOrder: number }[],
): Promise<void> => {
  await apiClient.post(`${BASE}/menus/${menuId}/reorder`, { items });
};

export const updateMenu = async (id: string, title: string): Promise<void> => {
  await apiClient.patch(`${BASE}/menus/${id}`, { title });
};

export const deleteMenu = async (id: string): Promise<void> => {
  await apiClient.delete(`${BASE}/menus/${id}`);
};
